import {Theme} from '@material-ui/core';
import {makeStyles} from '@material-ui/styles';
import React, {useState} from 'react';
import useRouter from 'use-react-router';
import {LoadingOverlay} from '../../../components/LoadingOverlay/LoadingOverlay';
import {Page} from '../../../components/Page';
import PageHeader from '../../../components/PageHeader';
import SearchBar from '../../../components/SearchBar';
import {OPERATOR, WhereConstraints} from '../../../graphql/common.datatypes';
import {Group} from '../../../graphql/groups.datatypes';
import getGroups from './getGroups.api';
import GroupSearchFilter, {FilterValues} from './GroupSearchFilter';
import Results from './Results';
import getMyGroups from './getMyGroups.api';
import moment from 'moment';
import {formatGraphQlDate} from '../../../utils/formatters';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    padding: theme.spacing(3)
  },
  results: {
    marginTop: theme.spacing(3)
  }
}));

// export interface FilterValues {
// }

export const GroupListContent = (props: { isAdmin: boolean }) => {
    const classes = useStyles();
    const {isAdmin} = props;
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [page, setPage] = useState(1);
    const [orderBy] = useState([{column: 'created_at', order: 'DESC'}]);
    const [selectedGroup, setSelectedGroup] = useState<Group | undefined>();

    const initialFilters: FilterValues = {
        name: '',
        tour_resource_id: -1,
        hotel_resource_id: -1,
        start_date: '',
        end_date: '',
        booking_form_url: '',
        default_authorize_members_true: false,
        default_authorize_members_false: false,
        validated_true: false,
        validated_false: false,
        disabled_true: false,
        disabled_false: isAdmin,
        locked_true: false,
        locked_false: false,
    };
  const [filters, setFilters] = useState(initialFilters);

  const applyFilters = (newFilters: FilterValues) => {
    setFilters(newFilters);
    setWhere(getConstraintsFromFilters(newFilters));
  };

  const getConstraintsFromFilters = (newFilters: FilterValues) => {
    const whereConstraints: WhereConstraints = {
      AND: [],
    };

    if (newFilters.name !== '') whereConstraints.AND!.push({
      column: 'name',
      operator: OPERATOR.LIKE,
      value: `%${newFilters.name}%`
    });

    if (newFilters.tour_resource_id !== -1) whereConstraints.AND!.push({
      column: 'tour_resource_id',
      operator: OPERATOR.EQ,
      value: newFilters.tour_resource_id
    });

    if (newFilters.hotel_resource_id !== -1) whereConstraints.AND!.push({
      column: 'hotel_resource_id',
      operator: OPERATOR.EQ,
      value: newFilters.hotel_resource_id
    });

    if (newFilters.start_date) whereConstraints.AND!.push({
      column: 'start_date',
      operator: OPERATOR.GTE,
      value: newFilters.start_date
    });
    if (newFilters.end_date) whereConstraints.AND!.push({
      column: 'end_date',
      operator: OPERATOR.LTE,
      value: newFilters.end_date
    });

    if (newFilters.default_authorize_members_true) whereConstraints.AND!.push({
      column: 'default_authorize_members',
      operator: OPERATOR.EQ,
      value: true
    });
    if (newFilters.default_authorize_members_false) whereConstraints.AND!.push({
      column: 'default_authorize_members',
      operator: OPERATOR.EQ,
      value: false
    });
    if (newFilters.validated_true) whereConstraints.AND!.push({
      column: 'validated',
      operator: OPERATOR.EQ,
      value: true
    });
    if (newFilters.validated_false) whereConstraints.AND!.push({
      column: 'validated',
      operator: OPERATOR.EQ,
      value: false
    });
    if (newFilters.disabled_true) whereConstraints.AND!.push({column: 'disabled', operator: OPERATOR.EQ, value: true});
    if (newFilters.disabled_false) whereConstraints.AND!.push({
      column: 'disabled',
        operator: OPERATOR.EQ,
        value: false
    });
      if (newFilters.locked_true) whereConstraints.AND!.push({column: 'locked', operator: OPERATOR.EQ, value: true});
      if (newFilters.locked_false) whereConstraints.AND!.push({column: 'locked', operator: OPERATOR.EQ, value: false});

      if(!isAdmin){
          const threeYears = formatGraphQlDate(moment().subtract((365*3), 'days'));
          whereConstraints.AND!.push({
              column: 'end_date',
              operator: OPERATOR.GTE,
              value: threeYears
          });
      }

      return whereConstraints;
  };

    const [where, setWhere] = useState(getConstraintsFromFilters(initialFilters));
    const groupsData = isAdmin ? getGroups(rowsPerPage, page, where, orderBy) : getMyGroups(rowsPerPage, page, where, orderBy);
    const groups = isAdmin ? groupsData.data && groupsData.data.groups : groupsData.data && groupsData.data.myGroups;

    const handleSearch = (value: string) => {
        const whereConstraints: WhereConstraints = {
            OR: [
                {column: 'name', operator: OPERATOR.LIKE, value: `%${value}%`},
                {column: 'description', operator: OPERATOR.LIKE, value: `%${value}%`},
            ],
            AND: [
                {column: 'disabled', operator: OPERATOR.EQ, value: false},
            ],
    };
    setWhere(whereConstraints);
  };

  return (
      <React.Fragment>
        <SearchBar onSearch={handleSearch} filterContent={
          <GroupSearchFilter filters={filters} initialFilters={initialFilters} applyFilters={applyFilters}/>
        }
        />
        <LoadingOverlay loading={groupsData.loading}>
            <Results
                className={classes.results}
                groups={(groups && groups.data) ? groups.data : []}
                paginatorInfo={(groups && groups.paginatorInfo) ? groups.paginatorInfo : undefined}
                selectedGroup={selectedGroup}
                setSelectedGroup={setSelectedGroup}
                setPage={setPage}
                rowsPerPage={rowsPerPage}
                setRowsPerPage={setRowsPerPage}
                isAdmin={isAdmin}
            />
        </LoadingOverlay>
      </React.Fragment>
  );
};

export const MyGroupList = () => {
    const classes = useStyles();
    const title = 'My Groups';

    return (
        <Page className={classes.root} title={title}>
            <PageHeader section="groups" title={title}/>
            <GroupListContent isAdmin={false}/>
        </Page>
    );
};

const GroupList = () => {
    const classes = useStyles();
    const title = 'Groups';
    const {history} = useRouter();

    const handleNewGroup = () => {
        history.push('/groups/new');
    };

    return (
    <Page className={classes.root} title={title}>
        <PageHeader section="groups" title={title} action={{
            permission: 'groups.create',
            label: 'Add Group',
            onClick: handleNewGroup,
        }}/>
        <GroupListContent isAdmin={true}/>
    </Page>
  );
};

export default GroupList;
